// components/HeroSection.js
import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import posterImage from "../assets/preview-poster.jpg";
import posterPreview from "../assets/preview-poster.webp";
import goldenVideo from "../assets/golden-video-hero.mp4";
import Seo from "../components/Seo";
import styled from "styled-components";
import Button from "@mui/material/Button";

const HeroContainer = styled.div`
  position: relative; /* Creates a stacking context */
  min-height: 95vh;
  padding-top: 96px;
  height: 773px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  overflow: hidden;
`;

const VideoBackground = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -2;
  will-change: transform;
  backface-visibility: hidden;
`;

const VideoOverlayGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4));
  z-index: -1;
`;

const ContentContainer = styled.div`
  max-width: 100%;
  padding: 0 32px;
  z-index: 2;
`;

const MainHeadline = styled.h1`
  font-family: Montserrat;
  font-weight: 600;
  font-size: 48px;
  line-height: 64px;
  letter-spacing: 0%;
  text-align: center;
  color: #ffffff;
  margin: 0 0 24px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 42px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 34px;
  }
`;

const SubHeadline = styled.p`
  font-family: Source Sans Pro;
  font-weight: 400;
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0%;
  text-align: center;
  color: #afb6d2;
  margin: 0 0 48px 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 28px;
    margin-bottom: 36px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    line-height: 26px;
    margin-bottom: 32px;
  }
`;

const VideoOverlay = styled.div`
  position: absolute;
  bottom: 32px;
  right: 32px;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 5;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    bottom: 24px;
    right: 24px;
    width: 35px;
    height: 35px;
  }
`;

const PauseIcon = styled.div`
  width: 12px;
  height: 14px;
  position: relative;

  &::before,
  &::after {
    content: "";
    position: absolute;
    background: #ffffff;
    width: 2px;
    height: 14px;
    border-radius: 1px;
  }

  &::before {
    left: 0;
  }

  &::after {
    right: 0;
  }
`;

const PlayIcon = styled.div`
  width: 0;
  height: 0;
  border-top: 7px solid transparent;
  border-bottom: 7px solid transparent;
  border-left: 12px solid #ffffff;
`;

const GradientButton = styled(Button)`
  min-width: 130px;
  height: 56px;
  background: linear-gradient(
    106.71deg,
    #c69957 0%,
    #f7e096 47.6%,
    #a57632 93.27%
  ) !important;

  color: #1e1d1b !important;
  font-family: "Montserrat", sans-serif !important;
  font-size: 18px !important;
  font-weight: 500 !important;
  line-height: 24px !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  text-align: center;
  vertical-align: middle;
  white-space: nowrap !important;
  box-shadow: none !important;
  padding: 8px 20px !important;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.9;
    background: linear-gradient(
      106.71deg,
      #c69957 0%,
      #f7e096 47.6%,
      #a57632 93.27%
    ) !important;
    box-shadow: none !important;
  }
`;

const BottomOverlay = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 100px; /* Adjust height as needed */
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #0c1c2c 100%);
  z-index: 1;
`;

const HeroSection = ({ headline, subheadline }) => {
  const [paused, setPaused] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const videoRef = useRef(null);
  const navigate = useNavigate();

  const togglePlayback = () => {
    const video = videoRef.current;
    if (video) {
      if (paused) video.play();
      else video.pause();
      setPaused(!paused);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowVideo(true);
    }, 500); // Delay rendering of video to improve LCP
    return () => clearTimeout(timer);
  }, []);

  const limitWords = (text, maxWords = 14) =>
    text.split(" ").slice(0, maxWords).join(" ");

  const splitToLines = (text, lineLimit = 2) => {
    const words = text.split(" ");
    const lines = [];
    const wordsPerLine = Math.ceil(words.length / lineLimit);
    for (let i = 0; i < lineLimit; i++) {
      lines.push(
        words.slice(i * wordsPerLine, (i + 1) * wordsPerLine).join(" ")
      );
    }
    return lines;
  };

  const lines = splitToLines(limitWords(headline));

  return (
    <HeroContainer>
      <Seo
        title="Neuora – Smart AI for Luxury Retail"
        description="Turn behaviors into 3x more sales with Neuora's in-store behavioral intelligence."
        url="https://neuora.ai/"
      />
      {/* <VideoBackground
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster={posterImage}
      >
        <source src={goldenVideo} type="video/mp4" />
        Your browser does not support the video tag.
      </VideoBackground> */}

      {/* Lazy-loaded video */}
      {showVideo ? (
        <VideoBackground
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          poster={posterPreview}
        >
          <source src={goldenVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </VideoBackground>
      ) : (
        <img
          src={posterPreview}
          alt="preview"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: -2,
          }}
        />
      )}

      <VideoOverlayGradient />

      <ContentContainer>
        <MainHeadline>
          <span style={{ whiteSpace: "pre-line" }}>
            {lines.map((line, idx) => (
              <div key={idx}>{line}</div>
            ))}
          </span>
        </MainHeadline>

        <SubHeadline>{subheadline}</SubHeadline>

        <GradientButton onClick={() => navigate("/contact")}>
          BOOK DEMO
        </GradientButton>
      </ContentContainer>

      <VideoOverlay onClick={togglePlayback}>
        {paused ? <PlayIcon /> : <PauseIcon />}
      </VideoOverlay>

      <BottomOverlay />
    </HeroContainer>
  );
};

export default HeroSection;
